import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Link, Grid, CircularProgress, IconButton, Menu, MenuItem as MuiMenuItem, Divider } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useCN56Report, type CN56ReportData, type CN56ReportSection, type CN56DataItem } from './hooks';
import { CN56Table, type CN56Column } from './components';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import type { SortConfig, SortDirection } from '../../../common/components/Table/types';
import { 
  MainContent,
  ReportContainer,
  SectionContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  ActionButton,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton
} from './CN56Report.styled';

interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface GroupByState {
  mailSubclass: boolean;
  oeFrom: boolean;
  oeTo: boolean;
  month: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Sorted CN56Table wrapper component
const SortedCN56Table: React.FC<{
  data: any[];
  columns: CN56Column[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  rowBackgroundColor?: string | ((row: any) => string);
  summaryRows?: {
    subTotalRow?: any;
    weightProportionsAndIpkRow?: any;
    cn56SummaryRows?: {
      sampledFlows?: any;
      nonSampledFlows?: any;
      allFlows?: any;
    };
  };
}> = ({ data, columns, moduleName, viewMode, disableAutoSizing, pageSize, lazyLoad, lazyLoadHeight, lazyLoadingBatchSize, rowBackgroundColor, summaryRows }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string>('');

  // Helper function to parse numeric values with thousand separators
  const parseNumeric = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      // Remove thousand separators (apostrophes, backticks, commas, spaces) and parse
      const cleaned = value.replace(/['`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    const { column, direction: sortDirection } = sortConfig;
    const multiplier = sortDirection === 'ASC' ? 1 : -1;

    return [...data].sort((a, b) => {
      const aValue = a?.[column];
      const bValue = b?.[column];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return -1 * multiplier;
      if (bValue == null) return 1 * multiplier;

      // Try numeric comparison first (handles thousand separators)
      const aNum = parseNumeric(aValue);
      const bNum = parseNumeric(bValue);
      
      if (aNum !== null && bNum !== null) {
        return (aNum - bNum) * multiplier;
      }

      // String comparison for non-numeric values
      const aStr = String(aValue);
      const bStr = String(bValue);
      return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [data, sortConfig]);

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setSortAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const handleSortSelect = (direction: SortDirection) => {
    if (direction === sortConfig?.direction && sortConfig?.column === activeColumn) {
      // Clear sort if clicking the same direction
      setSortConfig(undefined);
    } else {
      setSortConfig({ column: activeColumn, direction });
    }
    setSortAnchorEl(null);
  };

  const handleCloseSortMenu = () => {
    setSortAnchorEl(null);
  };

  // Function to get sort icon for a column
  const getSortIcon = (column: string) => {
    if (sortConfig?.column === column) {
      return sortConfig.direction === 'ASC' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
    }
    return <MoreVertIcon sx={{ fontSize: 16 }} />;
  };

  // Create enhanced columns with sortable headers
  const enhancedColumns = useMemo(() => {
    return columns.map(column => ({
      ...column,
      Header: (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
          {column.Header}
          <IconButton
            size="small"
            onClick={(e) => handleSortClick(e, column.accessor)}
            sx={{ p: 0.25 }}
          >
            {getSortIcon(column.accessor)}
          </IconButton>
        </Box>
      )
    }));
  }, [columns, sortConfig]);

  return (
    <>
      <CN56Table
        data={sortedData}
        columns={enhancedColumns}
        moduleName={moduleName}
        viewMode={viewMode}
        disableAutoSizing={disableAutoSizing}
        pageSize={pageSize}
        lazyLoad={lazyLoad}
        lazyLoadHeight={lazyLoadHeight}
        lazyLoadingBatchSize={lazyLoadingBatchSize}
        rowBackgroundColor={rowBackgroundColor}
        summaryRows={summaryRows}
      />
      
      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleCloseSortMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MuiMenuItem onClick={() => handleSortSelect('ASC')}>
          <ArrowUpwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Ascending
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleSortSelect('DESC')}>
          <ArrowDownwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Descending
        </MuiMenuItem>
        {sortConfig && sortConfig.column === activeColumn && (
          <>
            <Divider />
            <MuiMenuItem onClick={() => handleSortSelect(sortConfig.direction)}>
              <CloseIcon sx={{ mr: 1, fontSize: 16, color: '#F44336' }} />
              Clear Sort
            </MuiMenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

// Move mailColumns outside component and memoize it
const createMailColumns = (): CN56Column[] => [
  { accessor: 'mailCategory', Header: 'MC' },
  { accessor: 'outboundOe', Header: 'OE From' },
  { accessor: 'inboundOe', Header: 'OE To' },
  { accessor: 'mailSubclass', Header: 'MSC' },
  { accessor: 'month', Header: 'Month', textAlign: 'right', Cell: ({ value }: { value: number }) => value < 0 ? '' : value },
  { accessor: 'totalPGross', Header: 'P Gross', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalPNet', Header: 'P Net', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalGGross', Header: 'G Gross', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalGNet', Header: 'G Net', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalEGross', Header: 'E Gross', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalENet', Header: 'E Net', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalCGross', Header: 'C Gross', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalCNet', Header: 'C Net', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalPgeGross', Header: 'PGE Gross', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalPgeNet', Header: 'PGE Net', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalWeight5kg', Header: 'M-Bags total kg', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalWeight5kgActual', Header: 'M-Bags actual kg', textAlign: 'right', Cell: ({ value }: { value: number }) => value?.toFixed(1) || '0.0' },
  { accessor: 'totalRitems', Header: 'R Items', textAlign: 'right' },
  { accessor: 'totalVitems', Header: 'V Items', textAlign: 'right' },
  { accessor: 'totalExpressItems', Header: 'Express Items', textAlign: 'right' },
];

// Memoized Results Section Component
const ResultsSection = React.memo<{
  htmlResults: CN56ReportData;
  mailColumns: any[];
  direction: string;
}>(({ htmlResults, mailColumns, direction }) => {
  return (
    <Box sx={{ mt: 6 }}>
      {/* Main Results Title */}
      <Box sx={{ 
        borderBottom: '2px solid #E1E8ED',
        pb: 2,
        mb: 4
      }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          color: '#063142',
          fontSize: '24px',
          letterSpacing: '-0.5px'
        }}>
          Report Results
        </Typography>
        <Typography variant="body2" sx={{ 
          color: '#666',
          mt: 0.5,
          fontSize: '14px'
        }}>
          Generated CN56 report data tables
        </Typography>
      </Box>
      
      {htmlResults.map((section: CN56ReportSection, sectionIndex: number) => (
        <ReportSection 
          key={`section-${sectionIndex}`}
          section={section}
          sectionIndex={sectionIndex}
          mailColumns={mailColumns}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// Memoized Report Section Component
const ReportSection = React.memo<{
  section: CN56ReportSection;
  sectionIndex: number;
  mailColumns: any[];
  direction: string;
}>(({ section, sectionIndex, mailColumns, direction }) => {
  const getHeaderTitle = () => {
    const { headerGrouping } = section;
    const parts = [];
    
    if (headerGrouping.mailCategory) {
      parts.push(`Mail Category: ${headerGrouping.mailCategory}`);
    }
    if (headerGrouping.mailSubclass) {
      parts.push(`Mail Subclass: ${headerGrouping.mailSubclass}`);
    }
    if (headerGrouping.outboundOe) {
      parts.push(`Outbound OE: ${headerGrouping.outboundOe}`);
    }
    if (headerGrouping.inboundOe) {
      parts.push(`Inbound OE: ${headerGrouping.inboundOe}`);
    }
    if (headerGrouping.month) {
      parts.push(`Month: ${headerGrouping.month}`);
    }
    
    return parts.length > 0 ? parts.join(' | ') : `Section ${sectionIndex + 1}`;
  };

  return (
    <Box sx={{ 
      mb: 6,
      p: 3,
      border: '1px solid #E1E8ED',
      borderRadius: '8px',
      backgroundColor: '#FAFBFC'
    }}>
      {/* Section Title */}
      <Box sx={{ 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #E1E8ED'
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 600, 
          color: '#063142', 
          fontSize: '20px',
          letterSpacing: '-0.25px',
          mb: 1
        }}>
          {getHeaderTitle()}
        </Typography>
        {section.caption && (
          <Typography variant="body2" sx={{ 
            color: '#6C757D', 
            fontSize: '12px',
            fontFamily: 'monospace',
            backgroundColor: '#FFFFFF',
            padding: '4px 8px',
            borderRadius: '3px',
            border: '1px solid #DEE2E6',
            display: 'inline-block'
          }}>
            📄 {section.caption}
          </Typography>
        )}
      </Box>
      
      {/* Data Items Table with Summary Totals */}
      {section.items.length === 0 ? (
        <Box sx={{ 
          p: 4,
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px dashed #D1D9E0',
          mt: 3
        }}>
          <Typography sx={{ 
            fontStyle: 'italic', 
            color: '#666',
            fontSize: '14px'
          }}>
            No data items available for this section
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          mt: 3,
          '& .MuiPaper-root': {
            boxShadow: 'none',
            border: '1px solid #E1E8ED'
          }
        }}>
          <Typography variant="h6" sx={{ 
            fontWeight: 600, 
            color: '#063142',
            fontSize: '16px',
            mb: 2
          }}>
            Data Items ({section.items.length}) with Summary Totals
          </Typography>
          <SortedCN56Table
            data={section.items}
            columns={mailColumns}
            moduleName="CN56Report"
            viewMode={true}
            disableAutoSizing={false}
            pageSize={25}
            lazyLoad={section.items.length > 50}
            lazyLoadHeight={400}
            lazyLoadingBatchSize={25}
            rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
            summaryRows={{
              cn56SummaryRows: {
                sampledFlows: section.cn56ReportTotals.sampledTotal,
                nonSampledFlows: section.cn56ReportTotals.nonSampledTotal,
                allFlows: section.cn56ReportTotals.allFlowTotal
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
});



function CN56Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ppoValue, setPpoValue] = useState(() => {
    const ppoParam = searchParams.get('ppo');
    return ppoParam ? (ppoParam === 'All' ? 'All' : ppoParam) : 'All';
  });
  const [ppoFilter, setPpoFilter] = useState(() => {
    const tableParam = searchParams.get('table');
    return tableParam === 'outbound' ? 'Outbound' : 'Inbound';
  });
  const [fromYear, setFromYear] = useState(() => {
    const fromYearParam = searchParams.get('fromYear');
    return fromYearParam || '';
  });
  const [fromQuarter, setFromQuarter] = useState(() => {
    const fromQuarterParam = searchParams.get('fromQuarter');
    return fromQuarterParam || '';
  });
  const [toYear, setToYear] = useState(() => {
    const toYearParam = searchParams.get('toYear');
    return toYearParam || '';
  });
  const [toQuarter, setToQuarter] = useState(() => {
    const toQuarterParam = searchParams.get('toQuarter');
    return toQuarterParam || '';
  });

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<CN56ReportData | null>(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success'
  });

  // Get effective operators and cascading logic for e55 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e55', { fromYear, toYear, selectedPpo: ppoValue === 'All' ? undefined : ppoValue });

  // CN56 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError
  } = useCN56Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue && ppoValue !== 'All' ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach(quarter => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      // Ensure quarter is in the correct format (Q1, Q2, Q3, Q4)
      const quarter = fromQuarter.startsWith('Q') ? fromQuarter : `Q${fromQuarter}`;
      return `${fromYear}-${quarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      // Ensure quarter is in the correct format (Q1, Q2, Q3, Q4)
      const quarter = toQuarter.startsWith('Q') ? toQuarter : `Q${toQuarter}`;
      return `${toYear}-${quarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue && ppoValue !== 'All') {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('All');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e55 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e55' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('All');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('All');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Group By state
  const [groupBy, setGroupBy] = useState<GroupByState>({
    mailSubclass: false,
    oeFrom: false,
    oeTo: false,
    month: false,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/eno1');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = (value: string) => {
    setPpoValue(value);
  };

  // Handle year change with cascading behavior
  const handleFromChangeWithCascading = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Check if the currently selected DO is available for the new year range
        if (ppoValue && ppoValue !== 'All') {
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('All');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e55 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e55' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('All');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('All');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleServiceTypeChange = (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceType(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleGroupByChange = (field: keyof GroupByState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setGroupBy(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSelectAllServiceType = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    
    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllGroupBy = () => {
    const allSelected = groupBy.mailSubclass && 
                       groupBy.oeFrom && 
                       groupBy.oeTo && 
                       groupBy.month;
    
    if (allSelected) {
      // Deselect all
      setGroupBy({
        mailSubclass: false,
        oeFrom: false,
        oeTo: false,
        month: false,
      });
    } else {
      // Select all
      setGroupBy({
        mailSubclass: true,
        oeFrom: true,
        oeTo: true,
        month: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('All');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setGroupBy({
      mailSubclass: true,
      oeFrom: true,
      oeTo: true,
      month: true,
    });
  };

  // Map checkbox states to API parameter values
  const getServiceTypeValues = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getGroupByValues = (): string[] => {
    const values: string[] = [];
    if (groupBy.mailSubclass) values.push('ms');
    if (groupBy.oeFrom) values.push('out');
    if (groupBy.oeTo) values.push('in');
    if (groupBy.month) values.push('m');
    return values;
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'ALL');
    }

    // Add custom CN56Report-specific filters
    const serviceTypes = getServiceTypeValues();
    const groupByOptions = getGroupByValues();

    if (serviceTypes.length > 0) {
      newParams.append('serviceTypes', serviceTypes.join(','));
    }

    if (groupByOptions.length > 0) {
      newParams.append('groupBy', groupByOptions.join(','));
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromYear', fromYear);
      newParams.append('fromQuarter', fromQuarter);
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toYear', toYear);
      newParams.append('toQuarter', toQuarter);
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields - From and To are now required
    if (!fromYear || !fromQuarter) {
      showSnackbar('Please select a From period (Year and Quarter)');
      return;
    }

    if (!toYear || !toQuarter) {
      showSnackbar('Please select a To period (Year and Quarter)');
      return;
    }

    // Validate required fields
    const serviceTypes = getServiceTypeValues();
    const groupByOptions = getGroupByValues();

    if (serviceTypes.length === 0) {
      showSnackbar('Please select at least one Mail Category');
      return;
    }

    // Prepare API parameters
    const params = {
      serviceTypes,
      groupBy: groupByOptions,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      fromYear: parseInt(fromYear), 
      fromQuarter: parseInt(fromQuarter.replace('Q', '')),
      toYear: parseInt(toYear), 
      toQuarter: parseInt(toQuarter.replace('Q', '')),
      ...(ppoValue && ppoValue !== 'All' && { designatedOperator: ppoValue }),
      ...(ppoFilter && { table: ppoFilter }),
    };

    generateReport(params, {
      onSuccess: (response: any) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          if (response.data) {
            setHtmlResults(response.data);
            // Apply current filters to SectionBar when HTML is generated
            applyFiltersToSectionBar();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(`${format} Report downloaded successfully with filename: ${response.filename}`);
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        let errorMessage = 'An error occurred while generating the report';
        let errorStatus: string | undefined;
        
        // Check if it's an axios error
        if (axios.isAxiosError(error)) {
          // For blob responses, the error data might be a Blob containing JSON
          if (error.response?.data instanceof Blob) {
            try {
              const text = await error.response.data.text();
              const errorData = JSON.parse(text);
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            } catch (parseError) {
              // If parsing fails, use default message
              console.error('Failed to parse error response:', parseError);
            }
          } else if (error.response?.data && typeof error.response.data === 'object') {
            // For non-blob responses, check the data directly
            const errorData = error.response.data as { message?: string; status?: string };
            errorMessage = errorData.message || errorMessage;
            errorStatus = errorData.status;
          }
          
          // Check status code as fallback
          if (!errorStatus && error.response?.status === 404) {
            errorStatus = 'NOT_FOUND';
          }
        }
        
        // Show snackbar with appropriate message for all formats (HTML, PDF, XLS)
        if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No CN56 reports available') || errorMessage.includes('No CN56')) {
          showSnackbar(
            errorMessage || 'No CN56 reports available for the selected criteria',
            'warning'
          );
        } else {
          showSnackbar(errorMessage, 'error');
        }
      }
    });
  };

  // Memoize mailColumns to prevent recreation on every render
  const mailColumns = useMemo(() => createMailColumns(), []);

  // Track if initial URL sync has been performed using a ref
  const hasInitialUrlSyncRef = React.useRef(false);

  // Initial URL parameter sync when page loads (only once)
  useEffect(() => {
    if (hasInitialUrlSyncRef.current) return;
    
    const ppoParam = searchParams.get('ppo');
    const tableParam = searchParams.get('table');
    const typeParam = searchParams.get('type');
    const fromYearParam = searchParams.get('fromYear');
    const fromQuarterParam = searchParams.get('fromQuarter');
    const toYearParam = searchParams.get('toYear');
    const toQuarterParam = searchParams.get('toQuarter');

    // Sync PPO parameter
    if (ppoParam) {
      setPpoValue(ppoParam === 'All' ? 'All' : ppoParam);
    }

    // Sync direction parameter (check both 'type' and 'table' for backward compatibility)
    if (typeParam) {
      setPpoFilter(typeParam === 'outbound' ? 'Outbound' : 'Inbound');
    } else if (tableParam) {
      setPpoFilter(tableParam === 'outbound' ? 'Outbound' : 'Inbound');
    }

    // Sync from period parameters
    if (fromYearParam) {
      setFromYear(fromYearParam);
    }
    if (fromQuarterParam) {
      // Ensure quarter is stored in the correct format (Q1, Q2, Q3, Q4)
      const quarter = fromQuarterParam.startsWith('Q') ? fromQuarterParam : `Q${fromQuarterParam}`;
      setFromQuarter(quarter);
    }

    // Sync to period parameters
    if (toYearParam) {
      setToYear(toYearParam);
    }
    if (toQuarterParam) {
      // Ensure quarter is stored in the correct format (Q1, Q2, Q3, Q4)
      const quarter = toQuarterParam.startsWith('Q') ? toQuarterParam : `Q${toQuarterParam}`;
      setToQuarter(quarter);
    }
    
    hasInitialUrlSyncRef.current = true;
  }, [searchParams]);

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam === 'ALL' ? 'All' : ppoParam);
      }
    }
  }, [searchParams, htmlResults, ppoValue]);

  // Remove auto-regeneration - users must click export buttons manually


  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getGroupByButtonText = () => {
    const allSelected = groupBy.mailSubclass && 
                       groupBy.oeFrom && 
                       groupBy.oeTo && 
                       groupBy.month;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarGroupByChange = useCallback((values: string[]) => {
    setGroupBy({
      mailSubclass: values.includes('ms'),
      oeFrom: values.includes('out'),
      oeTo: values.includes('in'),
      month: values.includes('m'),
    });
  }, []);

  const handleSectionBarFromPeriodChange = useCallback((period: string) => {
    if (period) {
      const option = yearQuarterOptions.find(opt => opt.value === period);
      if (option) {
        setFromYear(option.year);
        setFromQuarter(option.quarter);
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('fromYear');
        newParams.delete('fromQuarter');
        newParams.delete('fromPeriod');
        newParams.append('fromYear', option.year);
        newParams.append('fromQuarter', option.quarter);
        newParams.append('fromPeriod', period);
        setSearchParams(newParams);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
      
      // Update URL parameters to clear from period
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('fromYear');
      newParams.delete('fromQuarter');
      newParams.delete('fromPeriod');
      setSearchParams(newParams);
    }
  }, [yearQuarterOptions, searchParams, setSearchParams]);

  const handleSectionBarToPeriodChange = useCallback((period: string) => {
    if (period) {
      const option = yearQuarterOptions.find(opt => opt.value === period);
      if (option) {
        setToYear(option.year);
        setToQuarter(option.quarter);
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('toYear');
        newParams.delete('toQuarter');
        newParams.delete('toPeriod');
        newParams.append('toYear', option.year);
        newParams.append('toQuarter', option.quarter);
        newParams.append('toPeriod', period);
        setSearchParams(newParams);
      }
    } else {
      setToYear('');
      setToQuarter('');
      
      // Update URL parameters to clear to period
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('toYear');
      newParams.delete('toQuarter');
      newParams.delete('toPeriod');
      setSearchParams(newParams);
    }
  }, [yearQuarterOptions, searchParams, setSearchParams]);

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
    
    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('type');
    newParams.append('type', direction.toLowerCase());
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    if (!htmlResults) return undefined;

    return {
      serviceTypes: getServiceTypeValues(),
      groupBy: getGroupByValues(),
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      direction: ppoFilter,
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onGroupByChange: handleSectionBarGroupByChange,
      onFromPeriodChange: handleSectionBarFromPeriodChange,
      onToPeriodChange: handleSectionBarToPeriodChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    serviceType,
    groupBy,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    getFromValue,
    getToValue,
    searchParams,
    setSearchParams,
    handleSectionBarServiceTypesChange,
    handleSectionBarGroupByChange,
    handleSectionBarFromPeriodChange,
    handleSectionBarToPeriodChange,
    handleSectionBarDirectionChange,
    handleExport,
    isGeneratingReport,
  ]);

  // Example: How to structure summary rows data for CN56Table
  // This would typically come from your API response or be calculated from the data
  /*
  const exampleSummaryRows = {
    cn56SummaryRows: {
      sampledFlows: {
        pOrSGross: 1250.75,
        pOrSNet: 1100.50,
        gGross: 890.25,
        gNet: 780.10,
        eGross: 450.80,
        eNet: 420.60,
        cGross: 200.30,
        cNet: 180.20,
        pgeGross: 2591.10,
        pgeNet: 2481.40,
        mBagsTotalKg: 125.00,
        mBagsActualKg: 120.50,
        rItems: 150,
        vItems: 75,
        expressItems: 25
      },
      nonSampledFlows: {
        pOrSGross: 850.25,
        pOrSNet: 750.15,
        gGross: 650.80,
        gNet: 600.40,
        eGross: 300.60,
        eNet: 280.45,
        cGross: 150.20,
        cNet: 140.10,
        pgeGross: 1951.85,
        pgeNet: 1771.10,
        mBagsTotalKg: 85.30,
        mBagsActualKg: 80.75,
        rItems: 95,
        vItems: 45,
        expressItems: 15
      },
      allFlows: {
        pOrSGross: 2101.00,
        pOrSNet: 1850.65,
        gGross: 1541.05,
        gNet: 1380.50,
        eGross: 751.40,
        eNet: 701.05,
        cGross: 350.50,
        cNet: 320.30,
        pgeGross: 4542.95,
        pgeNet: 4252.50,
        mBagsTotalKg: 210.30,
        mBagsActualKg: 201.25,
        rItems: 245,
        vItems: 120,
        expressItems: 40
      }
    }
  };
  */

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn56-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='CN56 Report'
            subtitle="e55 Weights"
            module="e55"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults,
              customCN56: !!htmlResults,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="cn56-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn56-report-main-content'>
            <ReportContainer data-testid="cn56-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="cn56-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="cn56-report-title">
                  CN56 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="cn56-report-header-actions">
                  {htmlResults && (
                    <ClearResultsButton 
                      variant="outlined" 
                      onClick={clearResults}
                      data-testid="cn56-report-clear-results-btn"
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="cn56-report-filters">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn56-report-ppo-direction-section">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn56-report-ppo-section">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="cn56-report-ppo-select"
                            >
                              <MenuItem value="All">All</MenuItem>
                              {isPpoLoading && (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <span>{option.label}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                              {ppoError && (
                                <MenuItem disabled>Error loading PPOs</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{ 
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px'
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          From YYYY.QQ
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChangeWithCascading(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">--Please Select--</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                  {/* Main Content Grid - Mail Category and Group By sections */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%'
                  }}>
                    {/* Mail Category */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Mail Category</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllServiceType}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label="A - Airmail Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label="B - SAL Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label="C - Surface Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label="D - Surface Priority"
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Group HTML and PDF report by */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Group HTML and PDF report by</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllGroupBy}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getGroupByButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={groupBy.mailSubclass}
                          onChange={handleGroupByChange('mailSubclass')}
                          label="Mail Subclass"
                        />
                        <CustomCheckbox
                          checked={groupBy.oeFrom}
                          onChange={handleGroupByChange('oeFrom')}
                          label="OE From"
                        />
                        <CustomCheckbox
                          checked={groupBy.oeTo}
                          onChange={handleGroupByChange('oeTo')}
                          label="OE To"
                        />
                        <CustomCheckbox
                          checked={groupBy.month}
                          onChange={handleGroupByChange('month')}
                          label="Month"
                        />
                      </CheckboxGroup>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {htmlResults && (
                        <ClearResultsButton 
                          variant="outlined" 
                          onClick={clearResults}
                          size="small"
                        >
                          Clear Results
                        </ClearResultsButton>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <ResetButton 
                        variant="contained" 
                        onClick={handleReset}
                      >
                        RESET
                      </ResetButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('HTML')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                      </ExportButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('PDF')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                      </ExportButton>
                      <ExportButton 
                        variant="contained" 
                        onClick={() => handleExport('XLS')}
                        disabled={isGeneratingReport}
                      >
                        {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                      </ExportButton>
                    </Box>
                  </ButtonContainer>
                </>
              )}

              {/* Results Section - Show tables directly under filters */}
              {htmlResults && (
                <ResultsSection htmlResults={htmlResults} mailColumns={mailColumns} direction={ppoFilter} />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="cn56-report-snackbar"
      />
    </Box>
  );
}

export default CN56Report; 